import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, ChevronLeft, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StockCellActions } from "@/components/StockCellActions";
import { StockReplenishDialog } from "@/components/StockReplenishDialog";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const TH = "text-[10px] uppercase tracking-widest text-slate-500 font-mono whitespace-nowrap";
const STATUS = {
  ok: { label: "Stokta", cls: "text-emerald-400 border-emerald-500/40" },
  critical: { label: "Kritik", cls: "text-amber-400 border-amber-500/40" },
  empty: { label: "Tükendi", cls: "text-rose-400 border-rose-500/40" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.ok;
  return <span className={`text-[10px] font-mono uppercase border rounded-sm px-1.5 py-0.5 ${s.cls}`}>{s.label}</span>;
}

// Rezerve / Opsiyon / Yolda kaynak detay penceresi
function SourceDialog({ src, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!src) { setRows(null); return; }
    setRows(null);
    setError("");
    const params = { product_id: src.product_id, variant_id: src.variant_id || "", warehouse_id: src.warehouse_id || "" };
    const req = src.kind === "incoming"
      ? api.get("/stocks/incoming-detail", { params })
      : api.get("/stocks/allocation-detail", { params: { ...params, kind: src.kind } });
    req.then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => { setError(formatApiError(e) || "Kaynak detayı yüklenemedi"); setRows([]); });
  }, [src]);
  const title = { incoming: "Yolda", reserved: "Rezerve", option: "Opsiyon" }[src?.kind] || "";
  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-800" data-testid="stock-source-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{title} — {src?.product_name} {src?.label ? `· ${src.label}` : ""}</DialogTitle>
        </DialogHeader>
        {rows === null ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-rose-400" data-testid="stock-source-error">{String(error)}</div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="stock-source-empty">Kayıt bulunamadı.</div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="stock-source-list">
            {rows.map((r, i) => <SourceRow key={i} kind={src?.kind} r={r} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const fmtDate = (d) => { const t = d ? new Date(d) : null; return t && !isNaN(t) ? t.toLocaleDateString("tr-TR") : "—"; };
const PO_STATUS = { ordered: "Sipariş Verildi", partial_received: "Kısmi Teslim" };

function SourceRow({ kind, r }) {
  if (kind === "incoming") {
    return (
      <div className="border border-slate-800 rounded-sm p-3 text-xs grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="stock-source-row">
        <div><div className="text-slate-500">Satın Alma Siparişi</div><div className="font-mono">{String(r.po_no || "—")}</div></div>
        <div><div className="text-slate-500">Tedarikçi</div><div className="text-slate-100">{String(r.supplier_name || "—")}</div></div>
        <div><div className="text-slate-500">Beden</div><div className="font-mono">{String(r.variant_sku || "—")}</div></div>
        <div><div className="text-slate-500">Durum</div><div>{PO_STATUS[r.status] || String(r.status || "—")}</div></div>
        <div><div className="text-slate-500">Sipariş / Teslim Alınan</div><div className="font-mono">{fmt(r.ordered_qty)} / {fmt(r.received_qty)}</div></div>
        <div><div className="text-slate-500">Kalan (Yolda)</div><div className="font-mono text-sky-400">+{fmt(r.open_qty)}</div></div>
        <div><div className="text-slate-500">Sipariş Tarihi</div><div>{fmtDate(r.created_at)}</div></div>
        <div><div className="text-slate-500">Beklenen Teslim</div><div>{fmtDate(r.expected_delivery_date)}</div></div>
        <div><div className="text-slate-500">Hedef Depo</div><div>{String(r.target_warehouse_name || "—")}</div></div>
      </div>
    );
  }
  return (
    <div className="border border-slate-800 rounded-sm p-3 text-xs flex flex-wrap gap-x-6 gap-y-1 items-center" data-testid="stock-source-row">
      <div className="font-semibold text-slate-100">{r.customer_name || "—"}</div>
      {r.order_no && <div className="font-mono text-slate-400">{r.order_no}</div>}
      {r.expires_at && <div className="text-slate-400">Bitiş: {new Date(r.expires_at).toLocaleDateString("tr-TR")}</div>}
      <div className="text-slate-400">{r.warehouse_name || "—"}</div>
      <div className="ml-auto font-mono font-semibold text-amber-400">{fmt(r.quantity)}</div>
    </div>
  );
}

// Tıklanabilir miktar (0 ise düz/pasif metin)
function Qty({ value, onClick, cls = "", prefix = "", testId }) {
  if (!value) return <span className="font-mono text-slate-600">0</span>;
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`font-mono underline decoration-dotted underline-offset-2 hover:opacity-80 transition-opacity ${cls}`}>
      {prefix}{fmt(value)}
    </button>
  );
}

const STICKY = "sticky left-0 z-10 bg-slate-900";
const CELL = "px-1.5 py-1 md:px-2 md:py-1.5 whitespace-nowrap";

// Tüm ekranlarda aynı kompakt tablo; sığmazsa yatay scroll, beden sütunu sticky
function SizeTable({ item, warehouses, matched, openSrc, openCell, openOrder }) {
  return (
    <div className="overflow-x-auto" data-testid={`grouped-sizes-${item.product_id}`}>
      <Table className="min-w-max">
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className={`${TH} ${CELL} ${STICKY} h-7`}>Beden</TableHead>
            {warehouses.map((w) => <TableHead key={w.id} className={`${TH} ${CELL} text-right h-7`}>{w.name}</TableHead>)}
            {["Fiziki", "Yolda", "Rezerve", "Opsiyon", "Kullanılabilir"].map((h) => (
              <TableHead key={h} className={`${TH} ${CELL} text-right h-7 ${h === "Kullanılabilir" ? "text-emerald-400" : ""}`}>{h}</TableHead>))}
            {openOrder && <TableHead className={`${TH} ${CELL} text-right h-7`}>Sipariş</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {item.sizes.map((s) => (
            <TableRow key={s.variant_id || "std"} data-testid={`grouped-size-row-${item.product_id}-${s.variant_id || "std"}`}
              className={`border-slate-800 hover:bg-slate-800/30 ${matched.has(s.variant_id) ? "bg-blue-500/10" : ""}`}>
              <TableCell className={`text-xs ${CELL} ${STICKY}`}>
                <span className="font-semibold text-slate-100">{s.label}</span>
                {s.sku && <span className="ml-1.5 font-mono text-[10px] text-slate-500">{s.sku}</span>}
              </TableCell>
              {warehouses.map((w) => (
                <TableCell key={w.id} className={`text-xs font-mono text-right ${CELL}`}>
                  <button type="button" onClick={() => openCell(item, s, w)} data-testid={`grouped-cell-${item.product_id}-${s.variant_id || "std"}-${w.id}`}
                    className="font-mono px-1 -mx-1 rounded-sm hover:bg-blue-500/15 hover:text-blue-300 transition-colors">
                    {fmt(s.by_warehouse[w.id])}
                  </button>
                </TableCell>
              ))}
              <TableCell className={`text-xs font-mono text-right text-slate-100 ${CELL}`}>{fmt(s.physical)}</TableCell>
              <TableCell className={`text-xs text-right ${CELL}`}><Qty value={s.incoming} prefix="+" cls="text-sky-400" onClick={() => openSrc("incoming", item, s)} testId={`grouped-incoming-${item.product_id}-${s.variant_id || "std"}`} /></TableCell>
              <TableCell className={`text-xs text-right ${CELL}`}><Qty value={s.reserved} prefix="−" cls="text-amber-400" onClick={() => openSrc("reserved", item, s)} testId={`grouped-reserved-${item.product_id}-${s.variant_id || "std"}`} /></TableCell>
              <TableCell className={`text-xs text-right ${CELL}`}><Qty value={s.option} prefix="−" cls="text-violet-400" onClick={() => openSrc("option", item, s)} testId={`grouped-option-${item.product_id}-${s.variant_id || "std"}`} /></TableCell>
              <TableCell className={`text-xs font-mono font-bold text-right text-emerald-400 bg-emerald-500/5 ${CELL}`}>{fmt(s.available)}</TableCell>
              {openOrder && (
                <TableCell className={`text-right ${CELL}`}>
                  <button type="button" onClick={() => openOrder(item, s)} data-testid={`grouped-order-add-${item.product_id}-${s.variant_id || "std"}`}
                    className="text-[10px] font-mono uppercase tracking-wider border border-blue-500/40 text-blue-300 rounded-sm px-2 py-0.5 hover:bg-blue-500/15 transition-colors">
                    Talep Oluştur
                  </button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ProductRow({ item, warehouses, open, onToggle, openSrc, openCell, openOrder, placeOrder }) {
  const matched = new Set(item.matched_variant_ids || []);
  return (
    <div className="border-b border-slate-800" data-testid={`grouped-product-${item.product_id}`}>
      <button type="button" onClick={onToggle} data-testid={`grouped-product-toggle-${item.product_id}`}
        className="w-full text-left px-3 md:px-4 py-3 hover:bg-slate-800/40 transition-colors grid grid-cols-[18px_1fr] md:grid-cols-[18px_1fr_90px_90px_90px_80px] gap-x-3 gap-y-1 items-center">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <div className="min-w-0">
          <div className="text-sm text-slate-100 truncate">{item.product_name || "—"}</div>
          <div className="text-[10px] font-mono text-slate-500 flex gap-2 items-center">
            <span>{item.product_code || "—"}</span><span>· {item.sizes.length} beden</span>
            <span className="md:hidden"><StatusBadge status={item.status} /></span>
          </div>
        </div>
        <div className="col-start-2 md:col-start-auto flex md:block gap-4 text-xs font-mono md:text-right">
          <span className="md:hidden text-slate-500">Fiziki </span>{fmt(item.physical)}
        </div>
        <div className="col-start-2 md:col-start-auto flex md:block gap-4 text-xs font-mono md:text-right text-emerald-400">
          <span className="md:hidden text-slate-500">Kullanılabilir </span>{fmt(item.available)}
        </div>
        <div className="col-start-2 md:col-start-auto flex md:block gap-4 text-xs font-mono md:text-right text-sky-400">
          <span className="md:hidden text-slate-500">Yolda </span>{item.incoming ? `+${fmt(item.incoming)}` : "0"}
        </div>
        <div className="hidden md:block text-right"><StatusBadge status={item.status} /></div>
      </button>
      {open && (
        <div className="bg-slate-950/30 border-t border-slate-800">
          {placeOrder && (
            <div className="flex justify-end px-3 pt-2">
              <button type="button" onClick={() => placeOrder(item)} data-testid={`grouped-place-order-${item.product_id}`}
                className="text-[10px] font-mono uppercase tracking-wider bg-blue-600 hover:bg-blue-500 text-white rounded-sm px-3 py-1 transition-colors">
                Talep Oluştur
              </button>
            </div>
          )}
          <SizeTable item={item} warehouses={warehouses} matched={matched} openSrc={openSrc} openCell={openCell} openOrder={openOrder} />
        </div>
      )}
    </div>
  );
}

function Filters({ f, setF, warehouses, types }) {
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v === "all" ? "" : v, page: 1 }));
  const trig = "bg-slate-900 border-slate-800 text-slate-100 rounded-sm h-9 text-sm";
  return (
    <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 mb-4" data-testid="products-filters">
      <div className="relative col-span-2 md:col-span-1 md:w-80">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="products-search" placeholder="Ürün adı / kodu / SKU / barkod ara…" value={f.q}
          onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} className={`pl-9 ${trig}`} />
      </div>
      <Select value={f.warehouse_id || "all"} onValueChange={set("warehouse_id")}>
        <SelectTrigger data-testid="products-filter-warehouse" className={`${trig} md:w-44`}><SelectValue placeholder="Depo" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tüm Depolar</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={f.supply_type || "all"} onValueChange={set("supply_type")}>
        <SelectTrigger data-testid="products-filter-type" className={`${trig} md:w-40`}><SelectValue placeholder="Ürün Tipi" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tüm Tipler</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={f.stock_status || "all"} onValueChange={set("stock_status")}>
        <SelectTrigger data-testid="products-filter-stock" className={`${trig} md:w-40`}><SelectValue placeholder="Stok Durumu" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tümü</SelectItem><SelectItem value="ok">Stokta Var</SelectItem>
          <SelectItem value="critical">Kritik</SelectItem><SelectItem value="empty">Tükendi</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export default function GroupedStockView({ allWarehouses = [], onTotal, refreshKey = 0 }) {
  const [f, setF] = useState({ q: "", warehouse_id: "", supply_type: "", stock_status: "", page: 1 });
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openIds, setOpenIds] = useState({});
  const lastFilterKey = useRef("");
  const [src, setSrc] = useState(null);
  const [cell, setCell] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [orderRow, setOrderRow] = useState(null);
  const { can } = useAuth();

  useEffect(() => { const t = setTimeout(() => { setQ(f.q); setF((p) => (p.page === 1 ? p : { ...p, page: 1 })); }, 300); return () => clearTimeout(t); }, [f.q]);

  useEffect(() => {
    setLoading(true);
    api.get("/stocks/grouped", { params: { search: q, warehouse_id: f.warehouse_id, supply_type: f.supply_type, stock_status: f.stock_status, page: f.page, page_size: 50 } })
      .then(({ data: d }) => {
        setData(d);
        if (!q && !f.warehouse_id && !f.supply_type && !f.stock_status) onTotal?.(d.total);
        // Detaylar yalnız kullanıcı tıklamasıyla açılır; filtre/arama değişince kapanır, işlem sonrası yenilemede açık kalır
        const fkey = JSON.stringify([q, f.warehouse_id, f.supply_type, f.stock_status, f.page]);
        const sameFilter = lastFilterKey.current === fkey;
        lastFilterKey.current = fkey;
        setOpenIds((p) => {
          if (!sameFilter) return {};
          const keep = {};
          (d.items || []).forEach((it) => { if (p[it.product_id]) keep[it.product_id] = true; });
          return keep;
        });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [q, f.warehouse_id, f.supply_type, f.stock_status, f.page, refreshKey, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSrc = (kind, item, s) => setSrc({ kind, product_id: item.product_id, product_name: item.product_name, variant_id: s.variant_id, label: s.label, warehouse_id: f.warehouse_id });
  const openCell = (item, s, w) => setCell({
    product_id: item.product_id, product_name: item.product_name, variant_id: s.variant_id, label: s.label,
    warehouse_id: w.id, warehouse_name: w.name, physical: Number(s.by_warehouse[w.id] || 0),
    available: Number((s.available_by_warehouse || {})[w.id] || 0),
  });
  const openOrder = (item, s) => setOrderRow({ product_id: item.product_id, product_name: item.product_name, variant_id: s.variant_id, label: s.label, available: fmt(s.available) });
  // Ürün bazlı Talep Oluştur: tüm bedenler tek talepte (doğrudan PO / tedarikçi seçimi yok)
  const placeOrder = (item) => setOrderRow({
    product_id: item.product_id, product_name: item.product_name,
    sizes: item.sizes.map((s) => ({ variant_id: s.variant_id || "", label: s.label, available: fmt(s.available) })),
  });
  const activeWarehouses = allWarehouses.filter((w) => w.is_active !== false);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <>
      <Filters f={f} setF={setF} warehouses={allWarehouses} types={data?.supply_types || []} />
      <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden" data-testid="products-grouped-list">
        <div className="px-4 py-2 text-xs font-mono text-slate-500 border-b border-slate-800 flex items-center gap-3">
          <span data-testid="products-count">{data ? `${data.total} ürün` : "…"}</span>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />}
        </div>
        <div className="hidden md:grid grid-cols-[18px_1fr_90px_90px_90px_80px] gap-x-3 px-4 py-2 border-b border-slate-800">
          <span /><span className={TH}>Ürün</span>
          <span className={`${TH} text-right`}>Fiziki</span><span className={`${TH} text-right`}>Kullanılabilir</span>
          <span className={`${TH} text-right`}>Yolda</span><span className={`${TH} text-right`}>Durum</span>
        </div>
        {(data?.items || []).map((it) => (
          <ProductRow key={it.product_id} item={it} warehouses={data.warehouses} open={!!openIds[it.product_id]}
            onToggle={() => setOpenIds((p) => ({ ...p, [it.product_id]: !p[it.product_id] }))} openSrc={openSrc} openCell={openCell} openOrder={can("purchases", "add") ? openOrder : null} placeOrder={can("purchases", "add") ? placeOrder : null} />
        ))}
        {data && data.items.length === 0 && <div className="text-center text-sm text-slate-500 py-8">Kayıt bulunamadı.</div>}
        {data && pages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-slate-800 text-xs font-mono text-slate-400" data-testid="products-pagination">
            <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF((p) => ({ ...p, page: p.page - 1 }))} className="h-7 rounded-sm" data-testid="products-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
            <span data-testid="products-page-info">{f.page} / {pages}</span>
            <Button size="sm" variant="outline" disabled={f.page >= pages} onClick={() => setF((p) => ({ ...p, page: p.page + 1 }))} className="h-7 rounded-sm" data-testid="products-page-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </Card>
      <SourceDialog src={src} onClose={() => setSrc(null)} />
      <StockCellActions cell={cell} warehouses={activeWarehouses} canEdit={can("warehouse", "edit")}
        onClose={() => setCell(null)} onChanged={() => setReloadKey((k) => k + 1)} />
      <StockReplenishDialog row={orderRow} onClose={() => setOrderRow(null)} />
    </>
  );
}
